import React, { useState, useEffect } from 'react';
import { storesService } from '../services/stores';
import { Eye } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';

const FeaturedStores = () => {
  const [selectedStore, setSelectedStore] = useState(null);
  const [featuredStores, setFeaturedStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoading(true);
        const stores = await storesService.getFeaturedStores();
        setFeaturedStores(stores.slice(0, 5));
      } catch (error) {
        console.error('Error loading featured stores:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStores();
  }, []);

  return (
    <div className="px-6 py-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Tiendas destacadas</h2>
          <p className="text-[#A2A2A2]">Selección exclusiva para ti</p>
        </div>

        {/* Featured Stores Grid */}
        <div className="grid grid-cols-1 gap-4">
          {loading && (
            <div className="text-center text-white py-8">
              Cargando tiendas destacadas...
            </div>
          )}
          {!loading && featuredStores.length === 0 && (
            <div className="text-center text-[#A2A2A2] py-8">
              No hay tiendas destacadas disponibles
            </div>
          )}
          {!loading && featuredStores.map((store) => (
            <div
              key={store.id}
              onClick={() => setSelectedStore(store)}
              className="bg-black border border-white/10 rounded-3xl p-6 hover:border-white/30 transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
                    {store.name} <Eye className="w-4 h-4 text-white" />
                  </h3>
                  <p className="text-[#A2A2A2] text-sm mb-3">{store.category}</p>
                  
                  <div className="bg-white/5 rounded-2xl p-4 border border-white/10 inline-block">
                    <p className="text-white font-medium">
                      {store.benefits && store.benefits.length > 0
                        ? `${store.benefits[0].title}${store.benefits.length > 1 ? ` +${store.benefits.length - 1}` : ''}`
                        : 'Sin beneficio activo'
                      }
                    </p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="border-white/10 text-white bg-transparent hover:bg-white/10 rounded-full px-4 py-2 text-sm"
                >
                  Ver más
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Store Detail Dialog */}
      <Dialog open={!!selectedStore} onOpenChange={() => setSelectedStore(null)}>
        <DialogContent className="bg-black border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{selectedStore?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div>
              <p className="text-[#A2A2A2] text-sm mb-1">Categoría</p>
              <p className="text-white">{selectedStore?.category}</p>
            </div>
            
            <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
              <p className="text-[#A2A2A2] text-sm mb-2">Beneficios</p>
              {selectedStore?.benefits && selectedStore.benefits.length > 0 ? (
                <ul className="space-y-2">
                  {selectedStore.benefits.map((b) => (
                    <li key={b.id} className="text-white">
                      <div className="font-medium">{b.title}</div>
                      {b.description && <div className="text-[#A2A2A2] text-sm">{b.description}</div>}
                      <div className="text-[#A2A2A2] text-xs">Vigencia: {b.validUntil ?? '—'}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-white font-medium text-lg">Sin beneficio activo</p>
              )}
            </div>

            <div>
              <p className="text-[#A2A2A2] text-sm mb-2">Descripción</p>
              <p className="text-white">{selectedStore?.description}</p>
            </div>

            <div>
              <p className="text-[#A2A2A2] text-sm mb-2">Vigencia</p>
              <p className="text-white">Hasta {selectedStore?.validUntil}</p>
            </div>

            {selectedStore?.conditions && selectedStore.conditions.length > 0 && (
              <div>
                <p className="text-[#A2A2A2] text-sm mb-2">Condiciones</p>
                <ul className="space-y-1">
                  {selectedStore.conditions.map((condition, index) => (
                    <li key={index} className="text-white text-sm flex items-start gap-2">
                      <span className="text-[#A2A2A2]">•</span>
                      <span>{condition}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button
              onClick={() => setSelectedStore(null)}
              className="w-full bg-white text-black hover:bg-white/90 rounded-full py-6 transition-all hover:scale-105 active:scale-95"
            >
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeaturedStores;
